import {GetServerSideProps} from 'next'
import {defineStyles, useGLTranslation, useThemedStyles} from 'notability-services-common-frontend'
import {GALLERY_NOTE_NOT_FOUND_REFERRER} from 'notability-services-common-universal'
import {iOSAppStatusFromContext} from 'notability-services-next-backend'
import {useLoggedIn} from 'notability-services-next-frontend'
import React, {useEffect} from 'react'

import DrillDownNoteView, {gridHeaderHeight} from '../components/DrillDownNoteView'
import {GalleryLayout} from '../components/GalleryLayout'
import NoteGridLayout from '../components/NoteGridLayout'
import {SignInGate} from '../components/SignInGate'
import {config} from '../config'
import {useLocalNoteViewCount} from '../hooks/useLocalNoteViewCount'
import {ComponentWithGalleryContext} from '../util/GalleryContext'
import {sspWithGalleryContext} from '../util/gallerySSR'
import {getStrings} from '../util/localizationUtils'
import {getNoteProps, NoteDrillDownPageArgs} from '../util/NoteProps'

const styles = defineStyles((theme, font) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%'
  },

  subsectionHeading: {
    marginBottom: '10px',
    ...font.largeTitle,
    fontWeight: 700,
    color: theme.core.f1,
    textAlign: 'center',
    zIndex: 1,
    height: gridHeaderHeight
  },

  separator: {
    width: '105vw',
    marginLeft: 'calc(-50vw + 50% - 10px)',
    height: '36px',
    boxShadow: `0px -6px 6px -6px rgba(0, 0, 0, 0.15)`,
    color: 'transparent',
    borderStyle: 'none',
    borderWidth: '0px'
  }
}))

export default ComponentWithGalleryContext(NoteDrillDownPage)

export function NoteDrillDownView({
  similarNotes,
  currentNote,
  myCollections,
  collectionsWithNote
}: NoteDrillDownPageArgs) {
  const {t} = useGLTranslation()
  const s = useThemedStyles(styles)
  const {loggedIn} = useLoggedIn()
  const {recordView, isAtCap} = useLocalNoteViewCount()

  useEffect(() => {
    if (!loggedIn) recordView(currentNote.id)
    // Intentionally exclude `loggedIn` from deps: a session expiring or logging out mid-view
    // should not cause this note to be (re-)counted toward the cap.
  }, [currentNote.id, recordView])

  return (
    <>
      {isAtCap && <SignInGate animateIn={true} source="Note Drill Down" overlayStyle={{zIndex: 1}} scrollOffset={0} />}
      <DrillDownNoteView {...currentNote} myCollections={myCollections} collectionsWithNote={collectionsWithNote} />
      {similarNotes.length > 0 && (
        <>
          <hr style={s.separator} />
          <div style={s.subsectionHeading}>{t('More like this')}</div>
          {/* signInGateSource intentionally omitted: NoteDrillDownView owns the gate above. */}
          <NoteGridLayout notes={similarNotes} isEmpty={similarNotes.length === 0} showAuthorPopovers={true} />
        </>
      )}
    </>
  )
}

function NoteDrillDownPage(props: NoteDrillDownPageArgs) {
  return (
    <GalleryLayout contentType={'NonVirtualizedGrid'}>
      <NoteDrillDownView {...props} />
    </GalleryLayout>
  )
}

export const getServerSideProps: GetServerSideProps<NoteDrillDownPageArgs> =
  sspWithGalleryContext<NoteDrillDownPageArgs>(async (context) => {
    const serverTranslation = await getStrings(context)
    const [props] = await getNoteProps(context)

    if (!props) {
      // The app shows its own unavailable screen for a 404; a browser goes to the homepage, which explains.
      if (iOSAppStatusFromContext(context)) {
        return {notFound: true}
      }
      return {
        redirect: {
          destination: `${config.API_SERVICE_URL}/?referrer=${GALLERY_NOTE_NOT_FOUND_REFERRER}`,
          permanent: false
        }
      }
    }

    return {
      props: {
        ...props,
        ...serverTranslation
      }
    }
  })
